import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useAdminAccess } from '@/hooks/access'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import type { Department } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { ExportToExcel } from '../components/ExportToExcel'

import { DepartmentsListItem } from './DepartmentsListItem'

export const DepartmentsList: FC<{
  searchFilter: string
  departments: Department[]
  onClickDepartmentRow: ({
    departmentId,
    isOpen,
  }: {
    departmentId: number
    isOpen: boolean
  }) => void
}> = ({ searchFilter, departments, onClickDepartmentRow }) => {
  useAdminAccess()
  const profile = useProfile()
  const translate = useTranslation()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof departmentsParsedForSorting)[number]>()

  const { isLoading, isError } = useQuery(
    queries.adminDepartmentsList(),
    adminResources.getDepartmentsList
  )

  const getManagerName = ({ user, deputyUser }: Department) => {
    const userText = user ? `${user.firstName} ${user.lastName}` : ''
    const deputyText = deputyUser ? `${deputyUser.firstName} ${deputyUser.lastName}` : ''
    return `${userText} ${userText && deputyText && '-'} ${deputyText}`.trim()
  }

  const departmentsParsedForSorting = departments.map(dep => ({
    ...dep,
    managerName: getManagerName(dep),
    companyName: dep.customer?.name || '',
  }))
  const sortedList = getSortedList({
    list: departmentsParsedForSorting,
    querySort,
    querySortBy,
    locale,
  })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || departments.length === 0) {
    return <NoResults />
  }

  const excelExportData = departments.map(department => {
    const userText = department.user
      ? `${department.user.firstName} ${department.user.lastName}`
      : ''
    const deputyText = department.deputyUser
      ? `${department.deputyUser.firstName} ${department.deputyUser.lastName}`
      : ''
    const isDepartmentHead = Boolean(userText || deputyText)
    const isFullDepartmentHead = Boolean(userText && deputyText)
    let departmentHead = userText || deputyText
    if (!isDepartmentHead) departmentHead = ''
    if (isFullDepartmentHead) departmentHead = `${userText}, ${deputyText}`

    return {
      code: department.code,
      name: department.name,
      project: department.project,
      'Dept. Head': departmentHead,
      company: department.customer.name,
      currency: profile?.currency,
    }
  })

  return (
    <>
      <ExportToExcel data={excelExportData} filename={translate('Departments')} />
      <Table>
        <thead>
          <tr>
            <Th id="code" onClick={handleSort}>
              <Translation id="code" />
            </Th>

            <Th id="name" onClick={handleSort}>
              <Translation id="name" />
            </Th>

            <Th id="project" className="hidden sm:table-cell" onClick={handleSort}>
              <Translation id="project" />
            </Th>
            <Th id="managerName" className="hidden sm:table-cell" onClick={handleSort}>
              <Translation id="Dept. Head" />
            </Th>
            <Th id="companyName" className="hidden sm:table-cell" onClick={handleSort}>
              <Translation id="Company" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <DepartmentsListItem
              key={item.id}
              searchFilter={searchFilter}
              onClickDepartmentRow={() =>
                onClickDepartmentRow({ departmentId: item.id, isOpen: true })
              }
              {...item}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
